// ErrorBoundary/index.tsx
//
// A rede de proteção que este projeto não tinha.
//
// POR QUE ELE EXISTE: até este card, `grep -rn "ErrorBoundary|componentDidCatch"
// src/` não retornava NADA. Sem boundary, uma exceção em qualquer render
// desmonta a árvore inteira do React — a página inteira fica BRANCA, sem uma
// linha na tela dizendo o que houve. Foi assim que cinco sub-telas da
// Configuração ficaram invisíveis por meses: quem abria via um retângulo vazio
// e não tinha como saber que era erro, e não "ainda não tem nada cadastrado".
//
// O QUE ELE MUDA: nada, enquanto nada lança — `render()` devolve `children`
// puro, sem elemento a mais, sem estilo a mais. É o que permite ele entrar sem
// custar um pixel nas telas que já funcionam. Quando algo lança, no lugar do
// branco aparece um cartão com o NOME da sub-tela e a MENSAGEM LITERAL do erro.
//
// `componentDidCatch` também repassa para `console.error`: a mensagem no
// console é a prova que o gabarito e o card usam, e engolir o erro em silêncio
// trocaria uma tela branca por um bug mudo, que é pior.

import React from "react";
import * as AlfredoStyles from "../../styles/components/ErrorBoundary";

interface ErrorBoundaryProps {
  /** O nome da tela que quebrou, para o cartão dizer ONDE foi. */
  ondeEstou?: string;
  children: React.ReactNode;
}

interface ErrorBoundaryState {
  erro: Error | null;
}

class ErrorBoundary extends React.Component<
  ErrorBoundaryProps,
  ErrorBoundaryState
> {
  state: ErrorBoundaryState = { erro: null };

  static getDerivedStateFromError(erro: Error): ErrorBoundaryState {
    return { erro };
  }

  componentDidCatch(erro: Error, info: React.ErrorInfo) {
    // Não engolir: o console continua sendo onde a pilha aparece inteira.
    console.error(
      `[ErrorBoundary] ${this.props.ondeEstou || "tela"} quebrou:`,
      erro,
      info.componentStack
    );
  }

  componentDidUpdate(anteriores: ErrorBoundaryProps) {
    // Trocar de sub-tela tem que limpar o erro; senão o cartão gruda e a
    // próxima tela — que está sã — herda a falha da anterior.
    if (this.state.erro && anteriores.ondeEstou !== this.props.ondeEstou) {
      this.setState({ erro: null });
    }
  }

  render() {
    const { erro } = this.state;
    if (!erro) return this.props.children;

    return (
      <AlfredoStyles.Caixa>
        <AlfredoStyles.Titulo>
          <AlfredoStyles.Icone>report</AlfredoStyles.Icone>
          Esta tela quebrou{this.props.ondeEstou ? `: ${this.props.ondeEstou}` : ""}
        </AlfredoStyles.Titulo>
        <div>
          O erro está abaixo, com todas as letras. O resto do sistema continua
          funcionando — troque de aba para sair daqui.
        </div>
        <AlfredoStyles.Mensagem>
          {erro.name}: {erro.message}
        </AlfredoStyles.Mensagem>
      </AlfredoStyles.Caixa>
    );
  }
}

export default ErrorBoundary;
